import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import {
    FAMILY_CHIP_CLASS,
    OutputBody,
    PII_TYPE_FAMILY,
    type PiiEntry,
    type PiiType,
    type SanitizationStrategy,
    STRATEGY_LABEL,
    splitOutputIntoChunks,
} from '@client/components/admin/sanitize-text-pii/shared';
import { useTrpc } from '@client/hooks/api/useTrpc';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Collapse, Empty } from 'antd';
import { AlertTriangle, FileText, Wand2 } from 'lucide-react';
import { useState } from 'react';

type CompareResult = RouterOutputs['admin']['sanitization']['compareApproaches'];
type CompareItem = CompareResult['items'][number];
type CompareCell = CompareItem['results'][SanitizationStrategy];

const STRATEGY_ORDER: SanitizationStrategy[] = ['inhouse', 'microsoft-presidio'];

export const Route = createFileRoute('/admin-console/tools/sanitise-text-pii/compare')({
    staticData: {
        breadcrumb: {
            title: 'Complete compare',
        },
        ui: {
            useParentTitle: true,
        },
    },
    component: CompareSanitizationPage,
});

function CompareSanitizationPage() {
    const { trpc } = useTrpc();
    const [result, setResult] = useState<CompareResult | null>(null);

    const compareMutation = trpc.admin.sanitization.compareApproaches.useMutation({
        onSuccess(data) {
            setResult(data);
            const sampleNoun = data.items.length === 1 ? 'sample' : 'samples';
            message.success(`Compared ${data.items.length} ${sampleNoun} across both strategies`);
        },
        onError(error) {
            console.error('[Admin sanitize PII compare] Failed', error);
            message.error(error.message || 'Compare run failed. Please try again.');
        },
    });

    const handleCompare = () => {
        compareMutation.mutate();
    };

    const isPending = compareMutation.isPending;

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-6xl">
                <PageHeader />

                <div className="mt-4 rounded-md border border-separator bg-card p-4">
                    <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
                        <Button
                            className="ml-auto self-end"
                            type="primary"
                            icon={<Wand2 />}
                            loading={isPending}
                            disabled={isPending}
                            onClick={handleCompare}
                        >
                            Compare approaches
                        </Button>
                    </div>
                    {result && (
                        <p className="mt-3 text-muted-foreground text-xs">
                            Last run: {(result.durationMs / 1000).toFixed(1)} s · {result.okCount} ok ·{' '}
                            {result.errorCount} error
                        </p>
                    )}
                </div>

                {!result && !isPending && <EmptyState />}
                {isPending && !result && <RunningState />}

                {result && (
                    <div className="mt-4 space-y-4">
                        {result.items.map((item) => (
                            <SampleCard key={item.index} item={item} />
                        ))}
                    </div>
                )}
            </div>
        </PageController>
    );
}

function PageHeader() {
    return (
        <Collapse
            ghost
            className="-ml-3"
            items={[
                {
                    key: 'how',
                    label: <span className="text-xs">How it works</span>,
                    children: (
                        <p className="m-0 text-muted-foreground text-sm">
                            Runs a fixed set of sample texts through both strategies (in-house and Microsoft Presidio)
                            and shows the results side by side, so you can spot differences at a glance. Each run is
                            capped at 60 seconds; if one strategy fails on a sample, the rest still finish.
                        </p>
                    ),
                },
            ]}
        />
    );
}

function EmptyState() {
    return (
        <div className="mt-6 flex min-h-[300px] items-center justify-center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Run compare to see all samples side by side" />
        </div>
    );
}

function RunningState() {
    return (
        <div className="mt-6 flex flex-col items-center justify-center rounded-md border border-separator border-dashed bg-muted/40 p-12 text-center">
            <p className="font-semibold text-foreground text-sm">Running compare…</p>
            <p className="mt-1 max-w-sm text-muted-foreground text-xs">
                Each sample is sent to both strategies in parallel. The run aborts after 60 s.
            </p>
        </div>
    );
}

interface SampleCardProps {
    item: CompareItem;
}

function SampleCard({ item }: SampleCardProps) {
    return (
        <Card
            className="bg-card"
            title={
                <Space size="small">
                    <span className="font-mono text-muted-foreground text-xs tabular-nums">
                        {String(item.index + 1).padStart(2, '0')} / 10
                    </span>
                    <span className="font-semibold text-foreground text-sm">{item.description}</span>
                </Space>
            }
        >
            <div className="rounded-md border border-separator bg-muted/40 p-3">
                <div className="mb-2 flex items-center gap-2 text-2xs text-muted-foreground uppercase tracking-wide">
                    <FileText size={14} />
                    <span>Original</span>
                    <span className="ml-auto font-mono normal-case">{item.original.length.toLocaleString()} chars</span>
                </div>
                <pre className="m-0 max-h-[280px] overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-6">
                    {item.original}
                </pre>
            </div>

            <div className="mt-3 divide-y divide-separator">
                {STRATEGY_ORDER.map((strategy) => (
                    <StrategyRow
                        key={strategy}
                        strategy={strategy}
                        cell={item.results[strategy]}
                        original={item.original}
                    />
                ))}
            </div>
        </Card>
    );
}

interface StrategyRowProps {
    strategy: SanitizationStrategy;
    cell: CompareCell;
    original: string;
}

function StrategyRow({ strategy, cell, original }: StrategyRowProps) {
    return (
        <div className="grid grid-cols-1 gap-4 py-4 lg:grid-cols-[200px_1fr]">
            <div className="flex flex-col gap-2">
                <span className="inline-flex w-fit items-center rounded-sm bg-muted px-2 py-0.5 font-mono text-2xs text-foreground ring-1 ring-separator ring-inset">
                    {STRATEGY_LABEL[strategy]}
                </span>
                {cell.status === 'ok' ? (
                    <>
                        <CountChips counts={cell.counts} />
                        <span className="font-mono text-2xs text-muted-foreground">
                            {cell.durationMs.toLocaleString()} ms · {cell.entries.length} item
                            {cell.entries.length === 1 ? '' : 's'}
                        </span>
                    </>
                ) : (
                    <span className="inline-flex items-center gap-1 font-mono text-2xs text-error">
                        <AlertTriangle size={14} />
                        <span>Failed</span>
                    </span>
                )}
            </div>

            <div>
                {cell.status === 'error' ? (
                    <div className="rounded-md border border-error bg-error/10 p-3 font-mono text-error text-xs">
                        Error: {cell.message}
                    </div>
                ) : (
                    <>
                        <OutputBody
                            chunks={splitOutputIntoChunks(cell.text)}
                            rawText={cell.text}
                            showRaw={false}
                            entries={cell.entries}
                        />
                        <DiffStrip cell={cell} original={original} />
                    </>
                )}
            </div>
        </div>
    );
}

interface CountChipsProps {
    counts: Record<PiiType, number>;
}

function CountChips({ counts }: CountChipsProps) {
    const visible = (Object.entries(counts) as Array<[PiiType, number]>).filter(([, n]) => n > 0);

    if (visible.length === 0) {
        return <span className="font-mono text-2xs text-muted-foreground">No PII detected</span>;
    }

    return (
        <div className="flex flex-wrap gap-1">
            {visible.map(([type, n]) => {
                const family = PII_TYPE_FAMILY[type];
                const chipClass = FAMILY_CHIP_CLASS[family];
                return (
                    <span
                        key={type}
                        className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-px font-mono text-2xs ring-1 ring-inset ${chipClass}`}
                    >
                        <span>{type}</span>
                        <span className="opacity-60">×{n}</span>
                    </span>
                );
            })}
        </div>
    );
}

interface DiffStripProps {
    cell: Extract<CompareCell, { status: 'ok' }>;
    original: string;
}

function DiffStrip({ cell, original }: DiffStripProps) {
    const charDelta = cell.text.length - original.length;
    const sourceCounts = countSources(cell.entries);

    return (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-2xs tabular-nums">
            <DeltaPill value={charDelta} unit="chars" />
            <DeltaPill value={cell.entries.length} unit="PII" forcePositive />
            <span className="text-muted-foreground">
                {sourceCounts.regex} regex · {sourceCounts.llm} LLM · {sourceCounts.presidio} presidio
            </span>
        </div>
    );
}

function countSources(entries: PiiEntry[]): Record<'regex' | 'llm' | 'presidio', number> {
    const acc = { regex: 0, llm: 0, presidio: 0 };

    for (const entry of entries) {
        acc[entry.source] += 1;
    }

    return acc;
}

interface DeltaPillProps {
    value: number;
    unit: string;
    forcePositive?: boolean;
}

function DeltaPill({ value, unit, forcePositive }: DeltaPillProps) {
    const sign = forcePositive ? (value > 0 ? '+' : '') : value > 0 ? '+' : value < 0 ? '−' : '±';
    const magnitude = Math.abs(value);
    const tone =
        forcePositive && value > 0
            ? 'text-foreground'
            : value === 0
              ? 'text-muted-foreground'
              : value > 0
                ? 'text-success'
                : 'text-warning';

    return (
        <span className={`inline-flex items-center gap-1 rounded-sm bg-muted/60 px-1.5 py-px ${tone}`}>
            <span>
                {sign}
                {magnitude.toLocaleString()}
            </span>
            <span className="opacity-60">{unit}</span>
        </span>
    );
}
